<template>
  <div v-show="open" class="sensepass-container">
    <div id="sensepass-front-end" class="sensepass-iframe-container"></div>
    <slot />
    <Button theme-color="primary" type="button" @click="payClicked">Pay using our secure server</Button>
    <div v-if="config.visualizer" id="sensepass-front-end-visualizer" class="sensepass-visualizer"></div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watchEffect } from "vue";
import { Button } from "@progress/kendo-vue-buttons";

export interface SensePassConfig {
  mode: "sandbox" | "production";
  debug: boolean;
  visualizer: boolean;
  clientId: string;
  methodType?: "manual_capture" | "tokenize";
  theme: {
    material: string;
    sensepassEcommerce: string;
    config: {};
  };
}

export interface SensePassPayCompletedPayload {
  TransactionNumber: string;
}

export interface SensePassTransaction {
  TransactionNumber: string;
}

export interface IProps {
  open?: boolean;
  config: SensePassConfig;
  amount: number; // in cents, e.g. 1000 for $10.00
}

const props = defineProps<IProps>();

const emit = defineEmits<{
  (e: "payCompleted", payload: SensePassPayCompletedPayload): void;
  (e: "paymentSelected", payload: any): void;
  (e: "error", payload: any): void;
}>();

const sensepassSdk = ref<any | null>(null);
const sensepass = ref<any | null>(null);

const openSensePass = function () {
  if (!sensepassSdk.value) return;

  const spClient = sensepassSdk.value.config(props.config);
  sensepass.value = spClient.init();

  const callback = (payload: any) => console.debug(payload);
  const errorCallback = (payload: any) => {
    emit("error", payload);
    console.debug(payload);
  };
  sensepass.value.paymentSelected((payload: any) => {
    callback(payload);
    if (payload.state === "selected") emit("paymentSelected", payload);
  });
  sensepass.value.paying(callback);
  sensepass.value.paymentMethodValidationStatus(callback);
  sensepass.value.error(errorCallback);
};

const payClicked = function () {
  if (!sensepass.value) return;

  const callback = (payload: any) => {
    console.debug("SensePass.vue, payClicked:", payload);
    emit("payCompleted", payload);
  };
  // TODO: payload might be required for ACH payments
  // Also required for Google Pay and Apple Pay options if we are planning to enable them in the future
  // sensepass.value.pay(callback, {
  //   amount: props.amount,
  //   currency: "USD",
  // });
  sensepass.value.pay(callback);
};

watchEffect(() => {
  if (props.open) {
    openSensePass();
  }
});

onMounted(() => {
  if (window.SensePassFrontEndSDK) {
    sensepassSdk.value = window.SensePassFrontEndSDK;
    return;
  }
  let linkScript = document.createElement("script");
  linkScript.async = true;
  linkScript.setAttribute("src", "https://js.sensepass.com/scripts/sensepass-front-end@1.0.9.sdk.js");
  document.head.appendChild(linkScript);

  linkScript.onload = () => {
    sensepassSdk.value = window.SensePassFrontEndSDK;
  };
});
</script>

<style lang="scss">
.sensepass-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;

  .sensepass-visualizer {
    max-height: 300px;

    pre {
      max-height: 300px !important;
    }
  }

  .sensepass-iframe-container {
    width: 100%;

    iframe {
      width: 100%;
      height: 100%;
      min-height: 300px;
    }
  }
}
</style>
